import { TunnelStep } from '../../pages/tunnel/model/tunnel-step-type';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { CONFIG_CONTEXT } from '../../enum/config-context.enum';
import { logError } from 'src/app/services/helpers/sentry.helper';

export type TunnelNavigationState = {
  steps: TunnelStep[];
  currentStep: TunnelStep;
  currentStepIndex: number;
};

type BeforeNavigateHandler = (nextIndex: number, currentIndex: number) => boolean | Promise<boolean>;

const initialState: TunnelNavigationState = {
  steps: [],
  currentStep: undefined,
  currentStepIndex: 0,
};

@Injectable({
  providedIn: 'root',
})
export class TunnelNavigationStore {
  private readonly tunnelNavigationState$ = new BehaviorSubject<TunnelNavigationState>(initialState);
  private readonly onNextStep$ = new BehaviorSubject<void>(null);
  private readonly beforeNavigateHandlers: BeforeNavigateHandler[] = [];

  private get stepLength() {
    let state = this.getCurrentStateValue();
    return state.steps.length;
  }

  constructor() {}

  public get isTunnelValue(): boolean {
    let state = this.getCurrentStateValue();
    return state.steps.length > 1;
  }

  public getIsTunnel(): Observable<boolean> {
    return this.tunnelNavigationState$.pipe(map(data => data.steps.length > 1));
  }

  public getCurrentStep(): Observable<TunnelStep> {
    return this.tunnelNavigationState$.pipe(map(data => data.currentStep));
  }

  public getOnNextStep(): Observable<void> {
    return this.onNextStep$.asObservable();
  }

  public getCurrentStepIndex(): Observable<number> {
    return this.tunnelNavigationState$.pipe(map(data => data.currentStepIndex));
  }

  public getIsFinalStep(): Observable<boolean> {
    return this.tunnelNavigationState$.pipe(map(data => data.currentStepIndex === data.steps.length - 1));
  }

  public getIsFirstStep(): Observable<boolean> {
    return this.tunnelNavigationState$.pipe(map(data => data.currentStepIndex === 0));
  }

  public getStepsLength(): Observable<number> {
    return this.tunnelNavigationState$.pipe(map(data => data.steps.length));
  }

  public getStepsLengthValue(): number {
    return this.getCurrentStateValue().steps.length;
  }

  public getNextStep(): Observable<TunnelStep> {
    return this.tunnelNavigationState$.pipe(
      map(data => data.steps.find(x => x.index === data.currentStepIndex + 1) ?? data.steps[-1])
    );
  }

  public getPreviousStep(): Observable<TunnelStep> {
    return this.tunnelNavigationState$.pipe(
      map(data => data.steps.find(x => x.index === data.currentStepIndex - 1) ?? data.steps[0])
    );
  }

  public getSteps(): Observable<TunnelStep[]> {
    return this.tunnelNavigationState$.pipe(map(data => data.steps));
  }

  public getCurrentContext(): Observable<CONFIG_CONTEXT> {
    return this.tunnelNavigationState$.pipe(map(data => data.currentStep.context));
  }

  public getCurrentState(): Observable<TunnelNavigationState> {
    return this.tunnelNavigationState$.pipe(map(data => data));
  }

  public loadSteps(steps: TunnelStep[], startIndex: number = 0) {
    let state = this.getCurrentStateValue();
    state.steps = this.initIndexSteps(steps);
    try {
      this.navigateToStep(startIndex);
    } catch (error) {
      logError(error);
    }
  }

  public resetState() {
    this.updateState({
      steps: [],
      currentStep: undefined,
      currentStepIndex: 0,
    });
  }

  private initIndexSteps(steps: TunnelStep[]) {
    let index = 0;
    steps.forEach((step: TunnelStep) => (step.index = index++));
    return steps;
  }

  public navigateToStep(index: number) {
    let state = this.getCurrentStateValue();
    try {
      state.currentStepIndex = index;
      state.currentStep = state.steps[index];
      this.updateState(state);
    } catch (error) {
      logError(error);
    }
  }

  public registerBeforeNavigate(handler: BeforeNavigateHandler): () => void {
    this.beforeNavigateHandlers.push(handler);
    return () => {
      const index = this.beforeNavigateHandlers.indexOf(handler);
      if (index >= 0) this.beforeNavigateHandlers.splice(index, 1);
    };
  }

  public async navigateToStepWithGuards(index: number): Promise<void> {
    if (!(await this.canNavigateToStep(index))) return;

    this.navigateToStep(index);
  }

  public async canNavigateToStep(index: number): Promise<boolean> {
    const currentIndex = this.getCurrentStateValue().currentStepIndex;

    for (const handler of [...this.beforeNavigateHandlers]) {
      if ((await handler(index, currentIndex)) === false) return false;
    }

    return true;
  }

  public nextStep() {
    let state = this.getCurrentStateValue();
    if (state.currentStepIndex < this.stepLength - 1) {
      this.navigateToStep(state.currentStepIndex + 1);
    }
    this.onNextStep$.next();
  }

  public skipStep() {
    let state = this.getCurrentStateValue();
    if (state.currentStepIndex < this.stepLength - 2) {
      this.navigateToStep(state.currentStepIndex + 2);
    }
  }

  public previousStep() {
    let state = this.getCurrentStateValue();
    if (state.currentStepIndex > 0) {
      this.navigateToStep(state.currentStepIndex - 1);
    }
  }

  private updateState(partialState: Partial<TunnelNavigationState>): void {
    const currentState = this.tunnelNavigationState$.getValue();
    this.tunnelNavigationState$.next({
      ...currentState,
      ...partialState,
    });
  }

  public getCurrentStateValue(): TunnelNavigationState {
    return this.tunnelNavigationState$.getValue();
  }
}
